import { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, Checkbox, FormControlLabel, Typography } from "@mui/material";
import { formatOrderTimestamp } from "../pages/inner/orderTime";

type RiskStatus = {
  enabled: boolean;
  quote_currency: string;
  date_utc: string;
  pause: null | {
    reason: string;
    detected_at: string;
    order_ids: string[];
    base_delta_before_fees: string | null;
  };
  fees: { total_quote: string; currencies: Record<string, string>; unknown_fee_records: number };
};

const reasons: Record<string, string> = {
  unbalanced_execution: "Ноги исполнились несбалансированно или по разным ценам",
  execution_unconfirmed: "Не удалось подтвердить состояние обоих ордеров",
  second_order_unconfirmed: "Не удалось подтвердить отправку второго ордера",
  batch_submission_unconfirmed: "Batch-пара принята частично или результат отправки не подтверждён",
};

export function RiskGuardPanel({ connectionId, exchange }: { connectionId: string; exchange: string }) {
  const [status, setStatus] = useState<RiskStatus | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [reviewed, setReviewed] = useState(false);
  const url = `https://lukkypro.ru/api/v1/bots/${connectionId}/risk`;
  const request = useCallback(async (method = "GET", suffix = "", body?: object) => {
    const response = await fetch(url + suffix, {
      method, headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}`, "Content-Type": "application/json" },
      body: body ? JSON.stringify(body) : undefined,
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.detail || result.message || "Не удалось получить состояние защиты");
    return result as RiskStatus;
  }, [url]);

  useEffect(() => {
    if (exchange !== "GATE") return;
    let active = true;
    const refresh = async () => {
      try {
        const result = await request();
        if (active) { setStatus(result); setError(""); }
      } catch (e) { if (active) setError(e instanceof Error ? e.message : "Ошибка загрузки защиты"); }
    };
    refresh();
    const timer = setInterval(refresh, 20000);
    return () => { active = false; clearInterval(timer); };
  }, [exchange, request]);

  useEffect(() => {
    if (status && !dirty) setEnabled(status.enabled);
  }, [status, dirty]);

  useEffect(() => { setReviewed(false); }, [status?.pause?.detected_at]);

  const submit = async (resume = false) => {
    setBusy(true); setError("");
    try {
      const result = resume ? await request("POST", "/resume")
        : await request("PUT", "", { enabled });
      setStatus(result); setDirty(false); setReviewed(false);
    } catch (e) { setError(e instanceof Error ? e.message : "Ошибка сохранения защиты"); }
    finally { setBusy(false); }
  };

  if (exchange !== "GATE") return null;
  return <Box sx={{ p: 2, my: 2, border: "1px solid", borderColor: status?.pause ? "error.main" : "divider", borderRadius: 2 }}>
    <Typography variant="h6">Защита баланса MM</Typography>
    {error && <Alert severity="error">{error}</Alert>}
    {status && <>
      <Typography variant="body2">Комиссии за {status.date_utc} (UTC): {Number(status.fees.total_quote).toFixed(4)} {status.quote_currency} в эквиваленте.</Typography>
      <Typography variant="caption">Справочный учёт. Остановки по комиссиям нет.</Typography>
      <Typography variant="body2">Списано: {Object.entries(status.fees.currencies).map(([currency, value]) => `${value} ${currency}`).join("; ") || "0"}.</Typography>
      {status.fees.unknown_fee_records > 0 && <Alert severity="warning">История комиссий неполная: {status.fees.unknown_fee_records} записей. Показанная сумма — только подтверждённая часть.</Alert>}
      {status.pause && <Alert severity="error" sx={{ my: 1 }}>
        <strong>Торговля приостановлена.</strong> {reasons[status.pause.reason] || status.pause.reason}<br />
        {formatOrderTimestamp(status.pause.detected_at)}<br />
        Ордера: {status.pause.order_ids.join(", ") || "—"}<br />
        {status.pause.base_delta_before_fees != null && <>Изменение токенов до комиссий: {status.pause.base_delta_before_fees}<br /></>}
        Автоматического обратного ордера или возобновления нет.
      </Alert>}
      <FormControlLabel control={<Checkbox checked={enabled} onChange={e => { setEnabled(e.target.checked); setDirty(true); }} />} label="Останавливать при несбалансированном или неподтверждённом исполнении" />
      <Button disabled={busy || !dirty} onClick={() => submit()} sx={{ mt: 1 }}>Сохранить защиту</Button>
      {status.pause && <Box>
        <FormControlLabel control={<Checkbox checked={reviewed} onChange={e => setReviewed(e.target.checked)} />}
          label="Я проверил исполнения, остатки открытых ордеров и доступные балансы" />
        <Button color="warning" disabled={busy || !reviewed || dirty} onClick={() => submit(true)}>Снять защитную паузу</Button>
      </Box>}
    </>}
  </Box>;
}
